import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Mail, UserPlus } from "lucide-react";
import { Page } from "@/components/layout";
import { ContentCard } from "@/components/ui/ContentCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLeaderScope } from "@/hooks/useLeaderScope";
import { emailMatchesDistrictDomain } from "@/lib/inviteEmailDomain";
import {
  cancelInvite,
  getInvitableSchools,
  getLicenseSummary,
  getPendingInvites,
  getSchoolDistrictDomain,
  resendInvite,
  sendInvite,
  type SchoolPickerOption,
} from "@/services/inviteTeammatesService";
import {
  INVITE_POOL_LABEL,
  INVITE_ROLE_LABEL,
  INVITE_ROLE_TO_POOL,
  type InviteRole,
  type LicenseSummary,
  type PendingInvite,
} from "@/types/inviteTeammates";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import { DistrictInviteAccordionPage } from "./DistrictInviteAccordionPage";
import { LicenseSummaryCard, PendingInvitesCard } from "./InviteAdminCards";

const ALL_ROLES: InviteRole[] = ["teacher", "support_staff", "leader"];

export const InviteTeammatesPage = () => {
  const { toast } = useToast();
  const { userData } = useAuth();
  const persona = deriveWorkspacePersona(userData);
  const { scope, isLoading: scopeLoading } = useLeaderScope();

  const [licenseSummary, setLicenseSummary] = useState<LicenseSummary | null>(null);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<SchoolPickerOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const [emailInput, setEmailInput] = useState("");
  const [roleInput, setRoleInput] = useState<InviteRole>("teacher");
  const [schoolIdInput, setSchoolIdInput] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  const [districtDomain, setDistrictDomain] = useState<string | null>(null);

  const isDistrictScope = scope.kind === "district" || scope.kind === "super_admin";

  const refresh = useCallback(async () => {
    if (scopeLoading) return;
    setIsLoadingData(true);
    try {
      const [summary, invites, schools] = await Promise.all([
        getLicenseSummary(scope),
        getPendingInvites(scope),
        getInvitableSchools(scope),
      ]);
      setLicenseSummary(summary);
      setPendingInvites(invites);
      setSchoolOptions(schools);
      if (!isDistrictScope && schools[0]) {
        setSchoolIdInput(schools[0].schoolId);
      } else if (isDistrictScope && !schoolIdInput && schools[0]) {
        setSchoolIdInput(schools[0].schoolId);
      }
    } finally {
      setIsLoadingData(false);
    }
  }, [scope, scopeLoading, isDistrictScope, schoolIdInput]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectedSchool = useMemo(
    () => schoolOptions.find((s) => s.schoolId === schoolIdInput) ?? schoolOptions[0],
    [schoolIdInput, schoolOptions]
  );

  useEffect(() => {
    let cancelled = false;
    if (!selectedSchool) {
      setDistrictDomain(null);
      return;
    }
    void getSchoolDistrictDomain(selectedSchool.schoolId).then((info) => {
      if (!cancelled) setDistrictDomain(info?.domain ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedSchool]);

  const selectedPool = INVITE_ROLE_TO_POOL[roleInput];
  const poolForSelected = licenseSummary?.pools[selectedPool];
  const isPoolFull = Boolean(poolForSelected && poolForSelected.used >= poolForSelected.total);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedSchool) {
      setFormError("Pick a school for this invite.");
      return;
    }

    if (districtDomain && !emailMatchesDistrictDomain(emailInput, districtDomain)) {
      setFormError(`Must use an email ending in @${districtDomain}.`);
      return;
    }

    setIsSending(true);
    try {
      const result = await sendInvite(
        scope,
        {
          email: emailInput,
          role: roleInput,
          schoolId: selectedSchool.schoolId,
          schoolName: selectedSchool.schoolName,
        },
        userData?.email ?? null
      );

      if (!result.ok) {
        setFormError(result.errorMessage ?? "Could not send invite.");
        toast({
          title: "Invite not sent",
          description: result.errorMessage ?? "Could not send invite.",
          variant: "destructive",
        });
        return;
      }

      setEmailInput("");
      toast({
        title: "Invite sent",
        description: `${result.invite!.email} will get an email to join ${
          result.invite!.schoolName ?? "the team"
        }.`,
      });
      await refresh();
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = async (invite: PendingInvite) => {
    setBusyInviteId(invite.id);
    try {
      const result = await cancelInvite(scope, invite.id);
      if (result.ok) {
        toast({
          title: "Invite cancelled",
          description: `${invite.email} will no longer receive a join link. Seat returned to the pool.`,
        });
        await refresh();
      } else {
        toast({
          title: "Could not cancel invite",
          description: result.errorMessage ?? "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setBusyInviteId(null);
    }
  };

  const handleResend = async (invite: PendingInvite) => {
    setBusyInviteId(invite.id);
    try {
      const result = await resendInvite(scope, invite.id);
      if (result.ok) {
        toast({
          title: "Invite resent",
          description: `Sent a fresh join link to ${invite.email}.`,
        });
        await refresh();
      } else {
        toast({
          title: "Could not resend invite",
          description: result.errorMessage ?? "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setBusyInviteId(null);
    }
  };

  if (persona === "district_leader") {
    return <DistrictInviteAccordionPage />;
  }

  return (
    <Page.Container>
      <Page.Header className="space-y-2">
        <h1 className="font-literata text-3xl font-semibold text-white md:text-4xl">
          Invite Teammates
        </h1>
        <p className="max-w-2xl font-inter text-sm leading-relaxed text-white/80 md:text-base">
          Bring teachers, support staff, and other leaders into HeyBodhi. Track seat usage, resend
          invites, and pull seats back when plans change.
        </p>
      </Page.Header>

      <Page.Content className="space-y-6">
        {scopeLoading || isLoadingData || !licenseSummary ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" aria-hidden />
          </div>
        ) : (
          <>
            <LicenseSummaryCard summary={licenseSummary} />

            <ContentCard className="space-y-5 p-5 md:p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <UserPlus className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <h2 className="font-literata text-base font-semibold text-stone-900 md:text-lg">
                    Send a new invite
                  </h2>
                  <p className="mt-1 font-inter text-xs text-stone-600 md:text-sm">
                    Inviting another leader uses a separate <em>leader seat</em>. Teachers and
                    support staff share the staff seat pool.
                  </p>
                </div>
              </div>

              <form
                className="grid gap-4 md:grid-cols-[2fr,1fr,1fr,auto] md:items-end"
                onSubmit={handleSubmit}
              >
                <FormField label="Email">
                  <div className="relative">
                    <Mail
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400"
                      aria-hidden
                    />
                    <Input
                      type="email"
                      autoComplete="off"
                      required
                      placeholder="teammate@school.org"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {districtDomain && (
                    <p className="font-inter text-xs text-stone-500">
                      Must use @{districtDomain}
                    </p>
                  )}
                </FormField>

                <FormField label="Role">
                  <Select
                    value={roleInput}
                    onValueChange={(v) => setRoleInput(v as InviteRole)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          {INVITE_ROLE_LABEL[role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label={isDistrictScope ? "School" : "School"}>
                  {isDistrictScope ? (
                    <Select value={schoolIdInput} onValueChange={setSchoolIdInput}>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a school" />
                      </SelectTrigger>
                      <SelectContent>
                        {schoolOptions.map((s) => (
                          <SelectItem key={s.schoolId} value={s.schoolId}>
                            {s.schoolName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-10 items-center rounded-md border border-stone-200 bg-stone-50 px-3 font-inter text-sm text-stone-700">
                      {selectedSchool?.schoolName ?? licenseSummary.scopeLabel}
                    </div>
                  )}
                </FormField>

                <Button
                  type="submit"
                  disabled={isSending || isPoolFull}
                  className="h-10 bg-[#1f3a30] text-white hover:bg-[#1f3a30]/90 md:w-auto"
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <UserPlus className="h-4 w-4" aria-hidden />
                  )}
                  Send invite
                </Button>

                {(formError || isPoolFull) && (
                  <p className="font-inter text-xs text-rose-600 md:col-span-4">
                    {formError ??
                      (isPoolFull
                        ? `${INVITE_POOL_LABEL[selectedPool]} pool is full. Cancel a pending invite or contact support to add seats.`
                        : null)}
                  </p>
                )}
              </form>
            </ContentCard>

            <PendingInvitesCard
              invites={pendingInvites}
              busyInviteId={busyInviteId}
              onCancel={handleCancel}
              onResend={handleResend}
              showSchool={isDistrictScope}
            />
          </>
        )}
      </Page.Content>
    </Page.Container>
  );
};

const FormField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <label className="flex min-w-0 flex-col gap-1.5">
    <span className="font-inter text-xs font-medium uppercase tracking-wide text-stone-500">
      {label}
    </span>
    {children}
  </label>
);
